import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { signInWithPopup } from "firebase/auth";
import SubscriptionService from "services/subscriptionService";
import { normalizeSubscriptionPayload } from "utils/subscriptionUtils";
import { AUTH_TOKEN, REFRESH_TOKEN } from "constants/AuthConstant";
import FirebaseService from "services/FirebaseService";
import AuthService from "services/AuthService";
import { initVendorFirebaseForStore } from "auth/storeFirebaseBootstrap";
import {
  startVendorPushNotifications,
  stopVendorPushNotifications,
} from "services/VendorPushNotificationService";
import {
  clearSubscriptionSession,
  saveSubscriptionCredentials,
  saveSubscriptionData,
} from "utils/subscriptionSession";

export const initialState = {
  loading: false,
  message: "",
  data: null,
  showMessage: false,
  redirect: "",
  token: localStorage.getItem(AUTH_TOKEN) || null,
};

export const signIn = createAsyncThunk(
  "auth/signIn",
  async (data, { rejectWithValue }) => {
    const { store_id, email, password } = data;
    try {
      localStorage.setItem("store_id", store_id);

      const response = await AuthService.login(store_id, email, password);
      console.log(response);

      if (response.access) {
        const token = response.access;

        localStorage.setItem(AUTH_TOKEN, response.access);
        localStorage.setItem(REFRESH_TOKEN, response.refresh);
        console.log("Saving storeId:", store_id);
        localStorage.setItem("store_id", store_id);
        localStorage.setItem("email", email);
        saveSubscriptionCredentials({
          storeId: store_id,
          email,
          password,
        });

        // Fetch subscription right after login so we can gate dashboard access
        // (e.g., Free Trail users can only access the subscription page).
        try {
          const subRes = await SubscriptionService.getDetails({
            storeId: store_id,
            email,
            password,
          });
          saveSubscriptionData(normalizeSubscriptionPayload(subRes.data));
        } catch (_e) {
          // Non-blocking: if subscription fetch fails, we keep login working.
        }
        try {
          await startVendorPushNotifications();
        } catch (_e) {
          // Keep login successful even if push registration fails.
        }

        return token;
      } else {
        // Handle error response with both error and error_ar
        if (response.error || response.error_ar) {
          return rejectWithValue({
            error: response.error,
            error_ar: response.error_ar,
          });
        }
        return rejectWithValue(response.message?.replace("Firebase: ", ""));
      }
    } catch (err) {
      // Check if error response contains error and error_ar
      const errorData = err.response?.data;
      if (errorData && (errorData.error || errorData.error_ar)) {
        return rejectWithValue({
          error: errorData.error,
          error_ar: errorData.error_ar,
        });
      }
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const signUp = createAsyncThunk(
  "auth/signUp",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;
    try {
      const response = await AuthService.login(email, password);
      if (response.user) {
        const token = response.user.refreshToken;
        localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
        return token;
      } else {
        return rejectWithValue(response.message?.replace("Firebase: ", ""));
      }
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const signOut = createAsyncThunk("auth/signOut", async () => {
  try {
    await stopVendorPushNotifications();
  } catch (_e) {
    // noop
  }
  const response = await FirebaseService.signOutRequest();
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  clearSubscriptionSession();
  return response.data;
});

export const signInWithGoogle = createAsyncThunk(
  "auth/signInWithGoogle",
  async (data, { rejectWithValue }) => {
    const { store_id } = data || {};
    if (!store_id) {
      return rejectWithValue("Store ID is required");
    }
    try {
      const { auth, googleAuthProvider } =
        await initVendorFirebaseForStore(store_id);
      const cred = await signInWithPopup(auth, googleAuthProvider);
      const idToken = await cred.user.getIdToken();

      const response = await AuthService.googleLogin(store_id, idToken);
      if (response?.access) {
        localStorage.setItem(AUTH_TOKEN, response.access);
        localStorage.setItem(REFRESH_TOKEN, response.refresh);
        localStorage.setItem("store_id", String(store_id));
        localStorage.setItem("email", response?.user?.email || "");
        try {
          await startVendorPushNotifications();
        } catch (_e) {
          // Keep login successful even if push registration fails.
        }
        return response.access;
      }

      return rejectWithValue(response?.error || "Google authentication failed");
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData && (errorData.error || errorData.error_ar)) {
        return rejectWithValue({
          error: errorData.error,
          error_ar: errorData.error_ar,
        });
      }
      return rejectWithValue(err.message || "Google authentication failed");
    }
  }
);

export const signInWithFacebook = createAsyncThunk(
  "auth/signInWithFacebook",
  async (_, { rejectWithValue }) => {
    const response = await FirebaseService.signInFacebookRequest();
    if (response.user) {
      const token = response.user.refreshToken;
      localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
      return token;
    } else {
      return rejectWithValue(response.message?.replace("Firebase: ", ""));
    }
  }
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthService.updateProfile(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthService.changePassword(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getProfile = createAsyncThunk(
  "auth/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.getProfile();

      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const register = createAsyncThunk(
  "auth/register",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthService.register(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.redirect = "/";
      clearSubscriptionSession();
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithFacebook.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithFacebook.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithFacebook.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })

      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
} = authSlice.actions;

export default authSlice.reducer;
